import { inject, ModuleWithProviders } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { UnAuthorizedComponent } from './core/components/unauthorized/unauthorized.component';
import { DashboardComponent } from './dashboard/components/dashboard/dashboard.component';
import { DashboardTabStore } from './dashboard/dashboard-tab.store';
import { AuthComponent } from './enduser/components/auth/auth.component';
import { siteGatewayRedirectGuard } from './routing/site-gateway-redirect.guard';
import { AnalyticsGuard } from './shared/guards/analytics.guard';
import { AuthGuard } from './shared/guards/auth.guard';
import { HeaderComponent } from './shell/components/header/header.component';

export const appRoutes: Routes = [
    {
        path: '',
        component: HeaderComponent,
        children: [
            { path: '', redirectTo: 'sites', pathMatch: 'full' },
            {
                path: '',
                component: DashboardComponent,
                children: [
                    {
                        path: 'sites',
                        loadComponent: () =>
                            import('./site-list/components/site-list/site-list.component').then(
                                (mod) => mod.SiteListComponent,
                            ),
                        canActivate: [AuthGuard],
                    },
                    {
                        path: 'link-requests',
                        loadComponent: () =>
                            import('./link-requests/components/link-requests-list/link-requests-list.component').then(
                                (mod) => mod.LinkRequestListComponent,
                            ),
                        canActivate: [AuthGuard],
                    },
                    {
                        path: 'alerts',
                        loadComponent: () =>
                            import('./alerts/components/alert-list/alert-list.component').then(
                                (mod) => mod.AlertListComponent,
                            ),
                        canActivate: [AuthGuard],
                    },
                ],
            },
            {
                path: 'resolve-alert',
                loadChildren: () => import('./alert-resolution/alert-resolution.routes').then((mod) => mod.routes),
                canActivate: [AuthGuard],
            },
            // The legacy inbox page was replaced by the notification center: keep old links working.
            { path: 'messages', redirectTo: 'notifications', pathMatch: 'full' },
            { path: 'messages/:id', redirectTo: 'notifications/view/:id' },
            {
                path: 'notifications',
                loadChildren: () => import('./notifications/notifications.routes').then((mod) => mod.routes),
                canActivate: [AuthGuard],
            },
            {
                path: 'alerts/config',
                loadChildren: () =>
                    import('./alerts-notification-config/alerts-notification-config.routes').then((mod) => mod.routes),
                canActivate: [AuthGuard],
            },
            {
                path: 'alerts/:alertId',
                loadComponent: () =>
                    import('./routing/alert-url-redirect.component').then((mod) => mod.AlertUrlRedirectComponent),
                canActivate: [AuthGuard],
            },
            {
                path: 'site/:idSite',
                canActivate: [AuthGuard, siteGatewayRedirectGuard],
                children: [],
            },
            {
                path: 'gateway',
                loadChildren: () => import('./site-detail/site-details.routes').then((mod) => mod.routes),
                canActivate: [AuthGuard],
                // The installation page links back to the dashboard tab it was opened from.
                resolve: { backLink: () => inject(DashboardTabStore).lastTabUrl() },
            },
        ],
    },
    {
        path: 'enduser/link-request',
        loadChildren: () => import('./enduser/enduser.routes').then((mod) => mod.routes),
    },
    {
        path: 'enduser/shared-site',
        loadChildren: () => import('./enduser/shared-site/shared-site.routes').then((mod) => mod.routes),
    },
    {
        path: 'query/intervention/:hash',
        loadComponent: () => import('./query/query.page').then((mod) => mod.QueryPage),
        canActivate: [AnalyticsGuard, AuthGuard],
    },
    {
        path: 'admin',
        loadChildren: () => import('./admin/admin.routes').then((mod) => mod.routes),
    },
    { path: 'unauthorized', component: UnAuthorizedComponent },
    {
        path: 'enduser/auth',
        pathMatch: 'full',
        component: AuthComponent,
        canActivate: [AuthGuard],
    },
    {
        path: '**',
        loadComponent: () => import('./core/pages/not-found/not-found.page').then((mod) => mod.NotFoundPage),
    },
];

export const routing: ModuleWithProviders<RouterModule> = RouterModule.forRoot(appRoutes, {
    useHash: false,
    bindToComponentInputs: true,
});
